import type { CSSProperties, ImgHTMLAttributes } from 'react';

export type ImageSource = { src: string; width: number };

type Props = {
  webp: ImageSource[];
  /** PNG (or other non-WebP) sources for `picture` and `<img>` fallback. */
  fallback: ImageSource[];
  sizes: string;
  /** Intrinsic dimensions of the *largest* asset (for `width` / `height` on `<img>` to limit CLS). */
  width: number;
  height: number;
  alt: string;
  className?: string;
  classNameImage?: string;
  styleImage?: CSSProperties;
  /** When true: `loading="eager"`, `fetchPriority="high"` (use once per page for the likely LCP image). */
  priority?: boolean;
} & Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  'alt' | 'src' | 'srcSet' | 'sizes' | 'width' | 'height' | 'loading' | 'decoding'
>;

function buildSrcSet(sources: ImageSource[]): string {
  return [...sources]
    .sort((a, b) => a.width - b.width)
    .map((s) => `${s.src} ${s.width}w`)
    .join(', ');
}

/** Best default `img src` for older browsers: prefer ~800w if present, else the largest. */
function pickDefaultSrc(fallback: ImageSource[]): string {
  if (fallback.length === 0) return '';
  const sorted = [...fallback].sort((a, b) => a.width - b.width);
  const target = 800;
  const atOrAbove = sorted.find((s) => s.width >= target);
  return (atOrAbove ?? sorted[sorted.length - 1]).src;
}

/**
 * Vite-hashed `import` paths + `<picture>`: WebP first, then PNG, one `<img>` for fallback.
 * Does not set `loading="lazy"` when `priority` (for likely LCP).
 */
export function ResponsiveImage({
  webp,
  fallback,
  sizes,
  width,
  height,
  alt,
  className,
  classNameImage,
  styleImage,
  priority = false,
  ...imgRest
}: Props) {
  if (fallback.length < 1) {
    return null;
  }
  if (webp.length < 1) {
    // Still render a single-format responsive img
    const srcSet = buildSrcSet(fallback);
    const defaultSrc = pickDefaultSrc(fallback);
    return (
      <img
        {...imgRest}
        className={classNameImage}
        style={styleImage}
        src={defaultSrc}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={priority ? 'high' : 'low'}
      />
    );
  }

  const webpSet = buildSrcSet(webp);
  const fallbackSet = buildSrcSet(fallback);
  const defaultSrc = pickDefaultSrc(fallback);

  return (
    <picture className={className}>
      <source type="image/webp" srcSet={webpSet} sizes={sizes} />
      <source type="image/png" srcSet={fallbackSet} sizes={sizes} />
      <img
        {...imgRest}
        className={classNameImage}
        style={{ width: '100%', height: 'auto', ...styleImage }}
        src={defaultSrc}
        srcSet={fallbackSet}
        sizes={sizes}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={priority ? 'high' : 'low'}
      />
    </picture>
  );
}
